import type { ToolPart } from './ApprovalActions';

// The user's files saved by Aura (crm-chat-api lib/artifacts.ts). Pure helpers,
// importable from node tests: no config, no fetch.

export const SAVE_ARTIFACT_TOOL_NAME = 'save_artifact';

export type ArtifactSummary = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  source: 'tool' | 'upload' | 'sandbox' | 'visual';
  chatId: string | null;
  createdAt: string;
};

// D14: an HTML file can hold CRM data and was written by the model, so a
// prompt injection can put script in it. Scripts may run (charts), but the
// frame gets an opaque origin (no allow-same-origin: no CRM cookies, storage
// or DOM), and no popups, forms or top navigation to carry data out. The
// frame navigating itself away is stopped by the wrapper below.
export const HTML_PREVIEW_SANDBOX = 'allow-scripts';

// D14, navigation: CSP cannot stop a script from navigating its frame away
// with data in the URL. The chat API stores every HTML file wrapped: the report
// sits in a sandboxed srcdoc iframe of a document whose CSP has frame-src
// 'none', which the browser applies to the report frame's navigations. Copied
// from crm-chat-api lib/artifacts.ts (HTML_WRAPPER_PREFIX / SUFFIX): keep them
// identical. If they drift, a stored file is wrapped a second time here, which
// is still safe.
const HTML_WRAPPER_CSP =
  "default-src 'none'; script-src 'unsafe-inline' https://cdnjs.cloudflare.com; " +
  "style-src 'unsafe-inline' https://fonts.googleapis.com; " +
  'font-src https://fonts.gstatic.com; img-src data: blob:; ' +
  "frame-src 'none'; form-action 'none'; base-uri 'none'";

export const HTML_WRAPPER_PREFIX =
  '<!DOCTYPE html><html><head><meta charset="utf-8">' +
  `<meta http-equiv="Content-Security-Policy" content="${HTML_WRAPPER_CSP}">` +
  '<meta name="viewport" content="width=device-width, initial-scale=1">' +
  '<style>html,body{margin:0;height:100%}' +
  'iframe{display:block;border:0;width:100%;height:100%}' +
  '@media print{html,body{height:auto}}</style></head><body>' +
  '<iframe id="report" title="Report" sandbox="allow-scripts" srcdoc="';

export const HTML_WRAPPER_SUFFIX =
  "\"></iframe><script>(()=>{const f=document.getElementById('report');" +
  "let h=0;addEventListener('message',e=>{if(e.source===f.contentWindow&&" +
  "typeof e.data?.auraReportHeight==='number')h=e.data.auraReportHeight});" +
  "addEventListener('beforeprint',()=>{if(h>0)f.style.height=Math.min(h,200000)+'px'});" +
  "addEventListener('afterprint',()=>{f.style.height=''})})()</script></body></html>";

// Exactly our wrapper: its prefix and suffix, and no quote in between, so the
// whole file content stays inside the srcdoc attribute.
const isWrappedHtml = (html: string): boolean =>
  html.length >= HTML_WRAPPER_PREFIX.length + HTML_WRAPPER_SUFFIX.length &&
  html.startsWith(HTML_WRAPPER_PREFIX) &&
  html.endsWith(HTML_WRAPPER_SUFFIX) &&
  !html
    .slice(HTML_WRAPPER_PREFIX.length, -HTML_WRAPPER_SUFFIX.length)
    .includes('"');

// The document the preview frame shows: the stored file when it is already
// our wrapper (every file saved since the wrapper shipped), else that file
// wrapped here, so an older or unexpected file can't navigate away either.
export const htmlPreviewDocument = (stored: string): string =>
  isWrappedHtml(stored)
    ? stored
    : `${HTML_WRAPPER_PREFIX}${stored
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')}${HTML_WRAPPER_SUFFIX}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

// An unknown source drops the row: keep in step with crm-chat-api
// ArtifactSource.
const SOURCES = new Set(['tool', 'upload', 'sandbox', 'visual']);

const isSource = (value: unknown): value is ArtifactSummary['source'] =>
  typeof value === 'string' && SOURCES.has(value);

export const parseArtifactSummary = (
  value: unknown,
): ArtifactSummary | null => {
  if (!isRecord(value)) {
    return null;
  }
  const { id, name, mimeType, sizeBytes, source, chatId, createdAt } = value;
  if (
    typeof id !== 'string' ||
    typeof name !== 'string' ||
    typeof mimeType !== 'string' ||
    typeof sizeBytes !== 'number' ||
    !isSource(source) ||
    !(chatId == null || typeof chatId === 'string') ||
    typeof createdAt !== 'string'
  ) {
    return null;
  }
  return {
    id,
    name,
    mimeType,
    sizeBytes,
    source,
    chatId: chatId ?? null,
    createdAt,
  };
};

export const parseArtifactList = (value: unknown): Array<ArtifactSummary> =>
  isRecord(value) && Array.isArray(value.artifacts)
    ? value.artifacts.flatMap(item => parseArtifactSummary(item) ?? [])
    : [];

// The file a finished save_artifact call created, or null while it runs or
// when it failed (`{ error }`): then the call stays a regular step.
export const savedArtifactOf = (part: ToolPart): ArtifactSummary | null =>
  part.toolName === SAVE_ARTIFACT_TOOL_NAME &&
  part.state === 'output-available' &&
  isRecord(part.output)
    ? parseArtifactSummary(part.output.artifact)
    : null;

export const SHOW_FILES_TOOL_NAME = 'show_files';

export type ShownFiles = {
  title: string | null;
  // PNG / JPEG / WebP / GIF: thumbnails that open the lightbox.
  images: Array<ArtifactSummary>;
  // Everything else (PDF, xlsx, zip, SVG…): a name / size / Download row.
  others: Array<ArtifactSummary>;
  // Deleted, another user's or tenant's, or a malformed id: the chat API
  // answers all of them the same way.
  missing: Array<string>;
};

// What a finished show_files call put on screen, or null while it runs or
// when it failed (`{ error }`): then the call stays a regular step. The output
// is metadata only; each file is fetched by id with the user's own session.
export const shownFilesOf = (part: ToolPart): ShownFiles | null => {
  if (
    part.toolName !== SHOW_FILES_TOOL_NAME ||
    part.state !== 'output-available' ||
    !isRecord(part.output) ||
    !Array.isArray(part.output.files) ||
    !Array.isArray(part.output.missing)
  ) {
    return null;
  }
  const files = part.output.files.flatMap(
    item => parseArtifactSummary(item) ?? [],
  );
  const title = part.output.title;
  return {
    title: typeof title === 'string' && title.trim() !== '' ? title : null,
    images: files.filter(isThumbnailImage),
    others: files.filter(file => !isThumbnailImage(file)),
    missing: part.output.missing.filter(
      (id): id is string => typeof id === 'string',
    ),
  };
};

export const saveArtifactErrorOf = (part: ToolPart): string | null =>
  part.toolName === SAVE_ARTIFACT_TOOL_NAME &&
  part.state === 'output-available' &&
  isRecord(part.output) &&
  typeof part.output.error === 'string'
    ? part.output.error
    : null;

const SIZE_UNITS = ['KB', 'MB', 'GB'];

export const formatFileSize = (bytes: number, locale: string): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: value < 10 ? 1 : 0,
  }).format(value);
  return `${formatted} ${SIZE_UNITS[unit]}`;
};

export type PreviewKind = 'html' | 'image' | 'pdf';

const PREVIEW_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

// SVG is not an image here: it can carry script, so it is only downloaded
// (the chat API serves it as an attachment too).
export const previewKindOf = (artifact: {
  name: string;
  mimeType: string;
}): PreviewKind | null => {
  if (
    artifact.mimeType === 'text/html' ||
    /\.html?$/i.test(artifact.name.trim())
  ) {
    return 'html';
  }
  if (PREVIEW_IMAGE_TYPES.has(artifact.mimeType)) {
    return 'image';
  }
  if (artifact.mimeType === 'application/pdf') {
    return 'pdf';
  }
  return null;
};

// Files shown as a thumbnail in the chat (save_artifact and show_files
// cards). Same rule as the Files preview, so never an SVG.
export const isThumbnailImage = (artifact: {
  name: string;
  mimeType: string;
}): boolean => previewKindOf(artifact) === 'image';

// The chat's files go only when the user ticked the box AND there are files
// to delete: an unchecked box, or a chat with none, keeps the plain delete.
export const chatDeleteUrl = (
  chatApiBase: string,
  chatId: string,
  options: { fileCount: number; deleteFiles: boolean },
): string =>
  options.deleteFiles && options.fileCount > 0
    ? `${chatApiBase}/${chatId}?deleteArtifacts=true`
    : `${chatApiBase}/${chatId}`;

// Lets only the latest of several async requests update the UI. start()
// supersedes every earlier request and returns its own isCurrent check;
// cancel() supersedes them all (e.g. the user closed the dialog).
export const createRequestGate = () => {
  let latest = 0;
  return {
    start: (): (() => boolean) => {
      latest += 1;
      const id = latest;
      return () => id === latest;
    },
    cancel: () => {
      latest += 1;
    },
  };
};
